
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Category, Resource } from '@/contexts/ResourcesContext';
import ResourceCard from './ResourceCard';
import { Button } from '@/components/ui/button';
import { ChevronRight, Pen, Check, X, Image, ImageOff } from 'lucide-react';
import { useResources } from '@/hooks/useResourceContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import ImageEditor from './ImageEditor';

interface CategoryBlockProps {
  category: Category;
  resources: Resource[];
}

const CategoryBlock: React.FC<CategoryBlockProps> = ({ category, resources }) => {
  const navigate = useNavigate();
  const { isAdmin, updateCategory, settings } = useResources();
  const [isEditing, setIsEditing] = useState(false);
  const [editedCategory, setEditedCategory] = useState({ ...category });
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isBannerImageDialogOpen, setIsBannerImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isImageSaved, setIsImageSaved] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [bannerImageError, setBannerImageError] = useState(false);
  
  const previewResources = resources.slice(0, 3);
  
  const getCategoryImage = (icon: string) => {
    switch (icon) {
      case 'phone':
        return "/lovable-uploads/4b374a38-6cfe-4f5b-9c73-99d35a0ab5fa.png"; 
      case 'mail':
        return "/lovable-uploads/722eea2a-f81d-4b2a-9014-f0aa7f25bddb.png"; 
      case 'users':
        return "https://images.unsplash.com/photo-1578574577315-3fbeb0cecdc2?w=800&h=300&fit=crop"; 
      case 'shield':
        return "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&h=300&fit=crop"; 
      default:
        return "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=300&fit=crop"; 
    }
  };

  const handleEditClick = () => {
    setEditedCategory({ ...category });
    setIsEditing(true);
  };

  const handleSaveCategory = () => {
    if (editedCategory.name.trim() === '') {
      toast.error("Category name cannot be empty");
      return;
    }
    updateCategory(category.id, editedCategory);
    setIsEditing(false);
    toast.success("Category updated successfully");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedCategory({ ...category });
  };

  const handleImageError = () => {
    console.log("Category icon image failed to load:", category.imageUrl);
    setImageError(true);
  };

  const handleBannerImageError = () => {
    console.log("Category banner image failed to load:", category.bannerImageUrl);
    setBannerImageError(true);
  };

  const handleImageUploadClick = () => {
    setIsImageDialogOpen(true);
    setIsImageSaved(false);
    setPreviewUrl(category.imageUrl || '');
  };

  const handleBannerImageUploadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBannerImageDialogOpen(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size exceeds 5MB. Please choose a smaller image.");
        return;
      }
      
      setSelectedImage(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          setPreviewUrl(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
      
      setIsImageSaved(false);
    }
  };

  const handleImageUpload = () => {
    if (selectedImage && previewUrl) {
      updateCategory(category.id, { 
        ...editedCategory,
        imageUrl: previewUrl
      });
      setIsImageDialogOpen(false);
      setIsImageSaved(true);
      setImageError(false);
      toast.success("Category image updated successfully");
    } else if (previewUrl) {
      updateCategory(category.id, { 
        ...editedCategory,
        imageUrl: previewUrl
      });
      setIsImageDialogOpen(false);
      setIsImageSaved(true);
      setImageError(false);
      toast.success("Category image updated successfully");
    } else {
      toast.error("Please select an image first");
    }
  };
  
  const handleBannerImageSave = (imageUrl: string) => {
    updateCategory(category.id, {
      ...category,
      bannerImageUrl: imageUrl
    });
    setBannerImageError(false);
    toast.success("Banner image updated successfully");
  };
  
  const handleResetToDefault = () => {
    const defaultImageUrl = getCategoryImage(category.icon);
    setPreviewUrl(defaultImageUrl);
    setSelectedImage(null);
    updateCategory(category.id, { 
      ...editedCategory,
      imageUrl: defaultImageUrl
    });
    setIsImageSaved(true);
    setImageError(false);
    toast.success("Reset to default image");
  };
  
  // Get default image URL if icon is missing or invalid
  const getIconImage = () => {
    if (!category.imageUrl || imageError) {
      return getCategoryImage(category.icon || 'default');
    }
    return category.imageUrl;
  };
  
  // Get banner image URL with fallbacks
  const getBannerImage = () => {
    if (!bannerImageError && category.bannerImageUrl) {
      return category.bannerImageUrl;
    }
    if (!imageError && category.imageUrl) {
      return category.imageUrl;
    }
    return getCategoryImage(category.icon || 'default');
  };
  
  // Check if user can edit (either admin or developer mode)
  const canEdit = isAdmin || settings.developerMode;
  
  return (
    <div className="mb-12">
      <div className="flex justify-between items-center mb-6">
        {isEditing ? (
          <div className="flex items-center gap-2 w-full">
            <Input 
              value={editedCategory.name}
              onChange={(e) => setEditedCategory({ ...editedCategory, name: e.target.value })}
              className="text-2xl font-bold"
            />
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleSaveCategory}
              className="text-green-500"
            >
              <Check className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleCancelEdit}
              className="text-red-500"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center">
            <h2 className="text-2xl font-bold text-gray-800">{category.name}</h2>
            {canEdit && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleEditClick}
                className="ml-2 text-gray-500 hover:text-brand-blue"
              >
                <Pen className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        {resources.length > 3 && (
          <Button 
            variant="ghost" 
            className="text-brand-blue hover:text-brand-darkBlue"
            onClick={() => navigate(`/category/${category.id}`)}
          >
            View All <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
      
      <div 
        className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer mb-6"
        onClick={() => navigate(`/category/${category.id}`)}
      >
        <div className="h-40 relative bg-gradient-to-br from-white/90 to-brand-blue/30">
          {bannerImageError ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-500">
              <ImageOff className="h-10 w-10 mb-2" />
              <span>Banner image not available</span>
            </div>
          ) : (
            <img 
              src={getBannerImage()}
              alt={category.name}
              className="w-full h-full object-cover"
              onError={handleBannerImageError}
            />
          )}
          <div className="absolute bottom-2 right-2 flex gap-2">
            {canEdit && (
              <Button
                variant="secondary"
                size="sm"
                className="bg-white/80 hover:bg-white"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBannerImageUploadClick(e);
                }}
                type="button"
              >
                <Image className="h-4 w-4 mr-1" /> Change Banner
              </Button>
            )}
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center mb-2">
            {imageError ? (
              <span className="mr-2 h-5 w-5 bg-gray-200 rounded-full flex items-center justify-center text-gray-500">
                <ImageOff className="h-3 w-3" />
              </span>
            ) : (
              <span className="mr-2">
                {category.icon === 'phone' && <span className="h-5 w-5 text-brand-blue">📞</span>}
                {category.icon === 'mail' && <span className="h-5 w-5 text-brand-blue">✉️</span>}
                {category.icon === 'users' && <span className="h-5 w-5 text-brand-blue">👥</span>}
                {category.icon === 'shield' && <span className="h-5 w-5 text-brand-blue">🛡️</span>}
                {!['phone', 'mail', 'users', 'shield'].includes(category.icon || '') && (
                  <img 
                    src={getIconImage()} 
                    alt={category.name}
                    className="h-5 w-5 object-cover rounded-full"
                    onError={handleImageError}
                  />
                )}
              </span>
            )}
            <h3 className="text-xl font-bold text-gray-800">{category.name}</h3>
          </div>
          {isEditing ? (
            <Textarea 
              value={editedCategory.description}
              onChange={(e) => setEditedCategory({ ...editedCategory, description: e.target.value })}
              className="mt-2"
            />
          ) : (
            <p className="text-gray-600">{category.description}</p>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {previewResources.map(resource => (
          <ResourceCard key={resource.id} resource={resource} />
        ))}
      </div>
      {resources.length === 0 && (
        <div className="bg-white p-6 rounded-lg text-center">
          <p className="text-gray-500">No resources available in this category.</p>
        </div>
      )}

      <ImageEditor
        isOpen={isBannerImageDialogOpen}
        onClose={() => setIsBannerImageDialogOpen(false)}
        onSave={handleBannerImageSave}
        originalImageUrl={category.bannerImageUrl || getIconImage()}
        defaultImageUrl={getCategoryImage(category.icon || 'default')}
        title="Change Banner Image"
        description="Upload a new banner image for this category. This image will appear at the top of the category page."
      />

      <Dialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Category Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="image-upload">Select Image</Label>
              <Input 
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-start">
                <span className="text-blue-500 mr-2">ℹ️</span>
                <div className="text-sm text-blue-700">
                  <p><strong>Recommended image dimensions:</strong></p>
                  <ul className="list-disc ml-4 mt-1">
                    <li>Banner images: 1200×400 pixels (3:1 aspect ratio)</li>
                    <li>Topic images: 800×600 pixels (4:3 aspect ratio)</li>
                    <li>Maximum file size: 5MB</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {previewUrl && (
              <div className="mt-4">
                <Label>Preview</Label>
                <div className="mt-2 border rounded-md overflow-hidden">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="w-full h-40 object-cover"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <div>
              <Button 
                variant="outline" 
                onClick={handleResetToDefault}
                className="mr-2"
              >
                Reset to Default
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsImageDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleImageUpload} className="bg-brand-blue hover:bg-brand-darkBlue">
                Upload Image
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryBlock;
